import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { applyPrefs, applyZoom, loadPrefs } from "./prefs";
import { lockWebChrome, markPlatform } from "./native-behaviors";
// Tokens de la refonte « Beeper » (docs/design/REFERENCE.md) — TOUJOURS en
// premier : source de vérité des couleurs/espacements/rayons/typo, deux modes.
import "./css/tokens.css";
// Composants génériques de la plateforme (ui-*) — briques de tout module.
import "./css/components.css";
// Base de l'app (utilitaires encore consommés), puis le shell messagerie.
import "./styles.css";
import "./cockpit.css";

// Applique zoom + apparence (thème .theme-*) mémorisés AVANT le rendu
// (évite le flash de mauvais thème, y compris sur l'écran de connexion).
applyZoom(loadPrefs().zoom);
applyPrefs(loadPrefs());
// Coupe les comportements « web » (long-press callout, double-tap zoom, overscroll).
lockWebChrome();
// Tague la plateforme (is-native / is-mac) pour le chrome adaptatif (barre haute).
markPlatform();

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root introuvable");
createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// ⌘R / Ctrl+R : la coquille native (Capacitor/WKWebView) n'a pas le raccourci du
// navigateur → on le rebranche pour voir les déploiements sans quitter l'app.
window.addEventListener("keydown", (e) => {
  if ((e.metaKey || e.ctrlKey) && (e.key === "r" || e.key === "R")) {
    e.preventDefault();
    location.reload();
  }
});

// Résilience post-déploiement : un déploiement purge les anciens chunks hashés.
// Un onglet resté ouvert qui navigue ensuite vers une vue lazy (Thread, Réglages…)
// échoue à charger son import dynamique — le serveur sert l'index.html en fallback,
// d'où un module au MIME "text/html" et une vue qui ne s'affiche pas. Vite émet
// `vite:preloadError` dans ce cas : on recharge pour récupérer l'index frais et ses
// chunks cohérents. Garde anti-boucle (max 1 rechargement / 10 s) au cas où le
// rechargement ne résout pas (offline, asset réellement manquant).
window.addEventListener("vite:preloadError", () => {
  const KEY = "aos:preload-reload-at";
  const last = Number(sessionStorage.getItem(KEY) ?? "0");
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    location.reload();
  }
});

// Auto-update : la coquille Catalyst restaure parfois l'ANCIENNE scène web au
// relancement (les déploiements ne l'atteignent pas tant qu'on ne re-navigue
// pas). index.html est servi en `no-cache`, donc le re-fetch renvoie toujours
// le dernier build : on compare le hash du bundle EN COURS (le <script> chargé)
// à celui de l'index servi, au retour au premier plan + périodiquement, et on
// recharge si ça a changé. Plus aucune manip après un déploiement. En dev (Vite),
// le bundle n'est pas un /assets/index-*.js hashé → runningHash null → no-op.
const runningHash =
  document
    .querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
    ?.src.match(/index-([\w-]+)\.js/)?.[1] ?? null;
let checkingUpdate = false;
async function checkForUpdate(): Promise<void> {
  if (!runningHash || checkingUpdate) return;
  checkingUpdate = true;
  try {
    const res = await fetch("/", { cache: "no-store" });
    if (!res.ok) return;
    const deployed = (await res.text()).match(/assets\/index-([\w-]+)\.js/)?.[1];
    if (deployed && deployed !== runningHash) location.reload();
  } catch {
    /* hors-ligne / index injoignable → on réessaiera au prochain focus */
  } finally {
    checkingUpdate = false;
  }
}
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void checkForUpdate();
});
window.addEventListener("focus", () => void checkForUpdate());
setInterval(() => void checkForUpdate(), 5 * 60_000); // filet si l'app reste ouverte
